// A color field in Flowmark's style: a 32px swatch with the native color
// picker laid invisibly over it, and a hex box beside it. The hex box takes
// "#4F46E5" or "4f46e5", commits on blur or Enter, and puts the last good
// value back when what was typed is not a 6-digit hex color.
import { useId, useState, type ReactNode } from 'react';
import { Field, INPUT, MONO, describedBy } from './InspectorKit';

/** "#RRGGBB" (upper case) for a 6-digit hex color typed with or without "#",
 *  else null. */
export function normalizeHex(text: string): string | null {
  const t = text.trim();
  const hex = t.startsWith('#') ? t.slice(1) : t;
  return /^[0-9a-f]{6}$/i.test(hex) ? `#${hex.toUpperCase()}` : null;
}

/** What the native picker can show: a valid color, or black. */
function pickerValue(color: string): string {
  return normalizeHex(color)?.toLowerCase() ?? '#000000';
}

export function ColorField({ label, value, fallback = '#000000', onChange, aside, hint, children }: {
  label: string;
  /** The stored color; empty shows `fallback` (what the page uses when unset). */
  value: string | undefined;
  fallback?: string;
  /** Gets "#RRGGBB" in upper case. */
  onChange: (hex: string) => void;
  /** Right side of the label row ("Same as brand", a live value). */
  aside?: ReactNode;
  hint?: ReactNode;
  /** Extra controls under the color row (swatches, a slider). */
  children?: ReactNode;
}) {
  const id = useId();
  const shown = (value || fallback).toUpperCase();
  const [draft, setDraft] = useState(shown);
  const [source, setSource] = useState(shown);
  // A new value from outside (the picker, a swatch, undo) replaces the draft.
  if (source !== shown) { setSource(shown); setDraft(shown); }

  function commit(text: string) {
    const hex = normalizeHex(text);
    if (!hex) { setDraft(shown); return; }
    setDraft(hex);
    if (hex !== shown) onChange(hex);
  }

  return (
    <Field label={label} htmlFor={id} aside={aside} hint={hint}>
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="relative h-8 w-8 shrink-0 overflow-hidden rounded-lg shadow-[inset_0_0_0_1px_var(--pe-hairline)] [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10"
          style={{ background: pickerValue(shown) }}>
          <input type="color" aria-label={`${label} picker`} value={pickerValue(shown)}
            className="absolute -inset-1 h-[calc(100%+8px)] w-[calc(100%+8px)] cursor-pointer opacity-0"
            onChange={(e) => { const hex = normalizeHex(e.target.value); if (hex && hex !== shown) onChange(hex); }} />
        </span>
        <input id={id} className={`${INPUT} ${MONO} min-w-0 flex-1 uppercase`} value={draft} maxLength={7} spellCheck={false}
          autoComplete="off" aria-describedby={describedBy(id, hint)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }} />
      </div>
      {children}
    </Field>
  );
}
